import React from 'react';
import { Box, Switch, styled } from '@mui/material';
import Color from 'color';
import type { RateModifierCategory } from '../../../../api/types/rater-x-preset';
import { useUnderwriterContext } from '../../provider/UnderwriterProvider';
import { isUnderwriterContext } from '../../types';
import StagedRowActions from '../../draft/components/StagedRowActions';
import StagedWas from '../../draft/components/StagedWas';
import { categoryCustomizationPath } from '../../draft/stagedFields';

const ModifierCategoryToggle = (props: ModifierCategoryToggleProps) => {
  const context = useUnderwriterContext();
  const canEdit = isUnderwriterContext(context);

  // Derived, never mirrored. This was `useState(props.modifier.isSelected)`,
  // which is invisible until the action stages: a staged toggle that the
  // underwriter then REJECTS reverts the reducer and leaves local state holding
  // the rejected position, so the switch goes on showing a change that is no
  // longer on the quote. Reading it back means the switch follows the draft —
  // and follows it back when an entry is dropped.
  //
  // The customization when the underwriter has made one, the rater's own value
  // otherwise: `isSelected` comes off the rater response and only moves when a
  // re-rate returns, which is later than the switch has to respond.
  const checked =
    context.state.categoryCustomizations
      ?.rateModifierCategoriesCustomizations?.[props.modifier.key]?.select ??
    props.modifier.isSelected;

  const handleChange = (event: any) => {
    context.handlers.setCategoryCustomization({
      type: 'rateModifierCategoriesCustomizations',
      payload: {
        [props.modifier.key]: {
          select: event.target.checked,
        },
      },
    });
  };

  return (
    // `flex-start`, not `center`: the staged line appears and disappears
    // beneath the switch, and centring would slide the switch up and down as
    // it did. The switch keeps one position in both states.
    <Box display="flex" alignItems="flex-start" gap="0.75rem">
      <i style={{ fontWeight: 500 }}>
        {checked
          ? props.modifier.isSelectedLabel
          : props.modifier.isUnselectedLabel}
      </i>

      {/* Everything staged sits UNDER the switch, right-aligned to it, and
          nothing staged sits beside it. Inline, the accept/reject pair pushed
          the switch sideways the moment anything staged, so the control moved
          depending on whether it had been touched.

          Right-aligned because `stagedWasSx` is `nowrap` — correct for the
          short money values every other staged control shows, and not this
          component's to change. Anchored to the right edge, a long label grows
          leftwards into the empty middle of the row instead of off the end. */}
      <Box
        sx={{
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'flex-end',
          marginLeft: 'auto',
        }}
      >
        <CustomSwitch
          checked={checked}
          onChange={handleChange}
          disabled={!canEdit}
          sx={{
            '& .MuiSwitch-track': {
              display: 'flex',
              justifyContent: checked ? 'flex-start' : 'flex-end',
              '&:before': {
                content: `"${checked ? 'On' : 'Off'}"`,
                color: '#fff',
              },
            },
          }}
        />
        <Box sx={{ display: 'flex', alignItems: 'center', gap: '0.25rem' }}>
          <StagedWas paths={[categoryCustomizationPath(props.modifier.key)]} />
          <StagedRowActions
            paths={[categoryCustomizationPath(props.modifier.key)]}
          />
        </Box>
      </Box>
    </Box>
  );
};

export default ModifierCategoryToggle;

type ModifierCategoryToggleProps = {
  readonly modifier: RateModifierCategory;
};

const CustomSwitch = styled(Switch)(({ theme, checked }) => ({
  width: 75,
  height: 30,
  padding: 0,
  '& .MuiSwitch-switchBase': {
    padding: 1,
    '&.Mui-checked': {
      transform: 'translateX(45px)',
    },
    '&.MuiSwitch-switchBase, &.Mui-checked': {
      color: '#fff',
    },
    '& + .MuiSwitch-track': {
      backgroundColor: Color(theme.config.colors.cowbellBlue)
        .lighten(0.15)
        .toString(),
      opacity: checked ? 1 : 0.75,
      border: `2px solid ${theme.palette.primary.dark}`,
    },
    '&.Mui-checked + .MuiSwitch-track, & + .MuiSwitch-track': {
      backgroundColor: Color(theme.config.colors.cowbellBlue)
        .lighten(0.15)
        .toString(),
      border: `2px solid ${theme.palette.primary.dark}`,
    },
  },
  '& .MuiSwitch-thumb': {
    width: 28,
    height: 28,
    borderRadius: '50%',
    boxShadow: 'none',
  },
  '& .MuiSwitch-track': {
    borderRadius: 17,
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
    padding: '0 10px',
    boxSizing: 'border-box',
  },

  '&:disabled': {
    opacity: 0.5,
  },
}));
